import { formatRating } from '@/services/utils/ratingFormat';
import MockService from '@/services/MockService';
import { dataSourceMixin } from '@/services/dataSourceMixin';
import heroImage from '@/assets/homepage-background.webp';
import ClubGeographyMap from '@/components/public/ClubGeographyMap/ClubGeographyMap.vue';
import PublicClubCard from '@/components/public/PublicClubCard/PublicClubCard.vue';
import { smallestImageUrl, imageSrcset, imageBackground } from '@/services/utils/imageVariants';
import {
  HOMEPAGE_WIDGET_CONFIG,
  RATING_CONTROL_ICON_MAP,
  SERVICE_BRAND,
  TOURNAMENT_SYSTEM_BADGE_CLASS_MAP,
} from '@/config';
import PlayerName from '@/components/PlayerName/PlayerName.vue';
import PublicClubCta from '@/components/public/PublicClubCta/PublicClubCta.vue';
import PublicPlayerAvatar from '@/components/public/PublicPlayerAvatar/PublicPlayerAvatar.vue';
import PublicSearchToolbar from '@/components/public/PublicSearchToolbar/PublicSearchToolbar.vue';
import PublicTournamentCard from '@/components/public/PublicTournamentCard/PublicTournamentCard.vue';
import PublicCarousel from '@/components/public/PublicCarousel/PublicCarousel.vue';
import IconChessVariant from '@/components/layout/icons/ChessVariant/ChessVariant.vue';
import {
  ChessVariant,
  CompetitionFormat,
  ControlType,
  RatingType,
  TournamentStatus,
} from '@/services/utils/enums';
import ClubService from '@/services/api/models/ClubService';
import TournamentService from '@/services/api/PublicTournamentService';
import { homepageFeatures } from './homepageContent';
import { formatDate as formatDateUtil } from '@/services/utils/dateFormat';
import { searchPublicItemsApi } from '@/services/publicSearchService';
import { getPublicStats, formatHomepageStats } from '@/services/publicStatsService';
import { localizeCountedStats } from '@/i18n/helpers';

const CONTROL_LABELS = {
  [ControlType.BULLET]: 'Bullet',
  [ControlType.BLITZ]: 'Blitz',
  [ControlType.RAPID]: 'Rapid',
  [ControlType.CLASSIC]: 'Classic',
};
const CONTROL_ICONS = {
  [ControlType.BULLET]: 'bi bi-rocket-takeoff',
  [ControlType.BLITZ]: 'bi bi-lightning-charge',
  [ControlType.RAPID]: 'bi bi-clock-history',
  [ControlType.CLASSIC]: 'bi bi-hourglass-split',
};
const EVENT_CONTROL_ICON_SIZES = {
  [ControlType.BULLET]: '14px',
  [ControlType.BLITZ]: '13px',
  [ControlType.RAPID]: '14px',
  [ControlType.CLASSIC]: '14px',
};
const RESULT_CONTROL_ICON_SIZES = {
  [ControlType.BULLET]: '17px',
  [ControlType.BLITZ]: '14px',
  [ControlType.RAPID]: '17px',
  [ControlType.CLASSIC]: '17px',
};

export default {
  mixins: [dataSourceMixin],
  name: 'HomePage',
  components: {
    ClubGeographyMap,
    PublicClubCard,
    PlayerName,
    PublicClubCta,
    PublicPlayerAvatar,
    PublicSearchToolbar,
    PublicTournamentCard,
    PublicCarousel,
    IconChessVariant,
  },

  data() {
    return {
      heroImage,
      searchQuery: '',
      submittedSearchQuery: '',
      submittedSearchResults: [],
      submittedSearchResultsCount: 0,
      isSearchDropdownVisible: false,
      searchHints: [
        { labelKey: 'search.hintTournaments', icon: 'bi bi-trophy' },
        { labelKey: 'search.hintClubs', icon: 'bi bi-shop-window' },
        { labelKey: 'search.hintCities', icon: 'bi bi-geo-alt' },
      ],
      stats: formatHomepageStats(),
      rawEvents: [],
      rawClubs: [],
      homepageFallback: false,
      homepageRequestId: 0,

      features: homepageFeatures,
      rawResults: [],
      rawMovers: [],
      rawPerformers: [],
    };
  },

  computed: {
    events() {
      return this.rawEvents.map((item) => this.normalizeHomepageTournament(item));
    },

    clubs() {
      return this.rawClubs.map((item) => this.normalizeHomepageClub(item));
    },

    results() {
      return this.rawResults.map((item) => this.normalizeHomepageResultItem(item));
    },

    movers() {
      return this.rawMovers.map((item) => this.normalizeHomepageResultItem(item));
    },

    performers() {
      return this.rawPerformers.map((item) => this.normalizeHomepageResultItem(item));
    },

    localizedStats() {
      return localizeCountedStats(this.stats, this.$t);
    },

    localizedFeatures() {
      return this.features.map((feature) => ({
        ...feature,
        title: this.$t(`home.features.${feature.translationKey}`),
      }));
    },

    searchPlaceholder() {
      return this.$t('search.publicPlaceholder');
    },

    searchAriaLabel() {
      return this.$t('search.publicAria');
    },
    visibleSearchResults() {
      return this.submittedSearchResults.slice(0, 6);
    },

    searchPageRoute() {
      return {
        name: 'PublicSearch',
        query: { q: this.submittedSearchQuery },
      };
    },

    heroBackground() {
      return `url(${this.heroImage})`;
    },

  },

  mounted() {
    this.reloadHomepage();
  },


  methods: {
    formatRating,
    imageBackground,
    async reloadHomepage() {
      const requestId = ++this.homepageRequestId;
      const isCurrentSource = this.captureDataSource();
      const source = this.dataSource;
      try {
        const loads = await Promise.allSettled([
          this.fetchHomepageStats(), this.fetchHomepageTournaments(),
          this.fetchHomepageClubs(), this.fetchHomepageResults(),
        ]);
        if (!isCurrentSource() || requestId !== this.homepageRequestId) return;
        const failure = loads.find(result => result.status === 'rejected');
        if (!failure) return;
        if (source !== 'db') throw failure.reason;

        // Wait for every API loader before replacing the entire homepage dataset.
        const [stats, events, clubs, results] = await Promise.all([
          MockService.loadHomepageStats(),
          MockService.loadHomepageTournaments(HOMEPAGE_WIDGET_CONFIG.tournamentsLimit),
          MockService.loadHomepageClubs(HOMEPAGE_WIDGET_CONFIG.clubsLimit),
          MockService.loadHomepageResults(),
        ]);
        if (!isCurrentSource() || requestId !== this.homepageRequestId) return;
        this.homepageFallback = true;
        this.stats = stats;
        this.rawEvents = events;
        this.rawClubs = clubs;
        this.rawResults = results.results;
        this.rawMovers = results.movers;
        this.rawPerformers = results.performers;
      } catch (error) {
        console.error('[HomePage] Failed to load homepage', error);
      }
    },

    async fetchHomepageTournaments() {
      const source = this.dataSource;
      const isCurrentSource = this.captureDataSource();
      const tournaments = source === 'mock'
        ? await MockService.loadHomepageTournaments(HOMEPAGE_WIDGET_CONFIG.tournamentsLimit)
        : await TournamentService.getUpcomingPublic({
            throwOnError: true,
            limit: HOMEPAGE_WIDGET_CONFIG.tournamentsLimit,
            status: [TournamentStatus.CREATED, TournamentStatus.ONGOING],
          });
      if (isCurrentSource()) {
        this.homepageFallback = false;
        this.rawEvents = tournaments;
      }
    },

    async fetchHomepageClubs() {
      const source = this.dataSource;
      const isCurrentSource = this.captureDataSource();
      const clubs = source === 'mock'
        ? await MockService.loadHomepageClubs(HOMEPAGE_WIDGET_CONFIG.clubsLimit)
        : await ClubService.getAll({ limit: HOMEPAGE_WIDGET_CONFIG.clubsLimit, throwOnError: true });
      if (isCurrentSource()) this.rawClubs = clubs;
    },

    onDataSourceChanged() {
      this.homepageFallback = false;
      this.submittedSearchResults = [];
      this.submittedSearchResultsCount = 0;
      this.isSearchDropdownVisible = false;
      this.rawEvents = [];
      this.rawClubs = [];
      this.rawResults = [];
      this.rawMovers = [];
      this.rawPerformers = [];
      this.stats = formatHomepageStats();
      this.reloadHomepage();
    },

    async fetchHomepageStats() {
      const source = this.dataSource;
      const isCurrentSource = this.captureDataSource();
      const stats = source === 'mock'
        ? await MockService.loadHomepageStats()
        : await getPublicStats(['clubs', 'cities', 'tournaments', 'players']);
      if (isCurrentSource()) this.stats = stats;
    },

    async fetchHomepageResults() {
      const source = this.dataSource;
      const isCurrentSource = this.captureDataSource();
      let results, movers, performers;
      if (source === 'mock') {
        ({ results, movers, performers } = await MockService.loadHomepageResults());
      } else {
        [results, movers, performers] = await Promise.all([
          TournamentService.getPublicLatestWinners({ limit: HOMEPAGE_WIDGET_CONFIG.latestResultsLimit, throwOnError: true }),
          TournamentService.getPublicRatingMovers({
            throwOnError: true,
            limit: HOMEPAGE_WIDGET_CONFIG.ratingMoversLimit,
            periodDays: HOMEPAGE_WIDGET_CONFIG.ratingMoversPeriodDays,
          }),
          TournamentService.getPublicTopPerformances({
            throwOnError: true,
            limit: HOMEPAGE_WIDGET_CONFIG.performersLimit,
            periodDays: HOMEPAGE_WIDGET_CONFIG.performersPeriodDays,
          }),
        ]);
      }
      if (!isCurrentSource()) return;
      this.rawResults = results;
      this.rawMovers = movers;
      this.rawPerformers = performers;
    },

    normalizeHomepageTournament(tournament) {
      if (this.dataSource === 'mock' || this.homepageFallback) {
        return {
          badge: this.$t(`home.${tournament.badgeKey}`),
          system: this.$t(`search.systemLabels.${tournament.systemType}`),
          systemType: tournament.systemType,
          title: tournament.title,
          club: tournament.club,
          city: this.$t(`home.cityNames.${tournament.cityKey}`),
          date: tournament.date,
          time: tournament.time,
          chessVariant: tournament.chessVariant,
          chessVariantLabel: tournament.chessVariantLabel || tournament.chessVariant,
          control: tournament.control,
          controlType: tournament.controlType,
          controlIconComponent: this.getControlIconComponent(tournament.controlType),
          icon: tournament.icon,
          competitionFormatLabel: this.getCompetitionFormatLabel(tournament),
          competitionFormatIcon: this.getCompetitionFormatIcon(tournament),
          competitionFormatColor: this.getCompetitionFormatColor(tournament),
          ratedLabel: this.getRatedLabel(tournament),
          ratedIcon: this.getRatedIcon(tournament),
          ratedColor: this.getRatedColor(tournament),
          registrationText: tournament.registrationTextKey
            ? this.$t(`home.${tournament.registrationTextKey}`)
            : null,
          players: tournament.players,
          progress: tournament.progress,
          background: tournament.background,
          route: null,
        };
      }

      const controlType = tournament.controlType ?? tournament.getControlType?.() ?? null;
      const capacity = this.getTournamentCapacity(tournament);

      return {
        id: tournament.id,
        place: tournament.place,
        badge: this.getTournamentDateBadge(tournament.date),
        system: this.$t(`search.systemLabels.${tournament.system}`),
        systemType: tournament.system,
        title: tournament.name,
        club: tournament.publicClubName || this.$t('home.defaultClub'),
        city: tournament.publicClubCity || tournament.publicClubCountry || '',
        date: this.formatTournamentDate(tournament.date),
        time: this.formatTournamentTime(tournament.time),
        chessVariant: tournament.chessVariant,
        chessVariantLabel: this.getChessVariantLabel(tournament.chessVariant),
        control: this.getTournamentControlLabel(tournament),
        controlType,
        controlIconComponent: this.getControlIconComponent(controlType),
        icon: CONTROL_ICONS[controlType] || 'bi bi-clock',
        competitionFormatLabel: this.getCompetitionFormatLabel(tournament),
        competitionFormatIcon: this.getCompetitionFormatIcon(tournament),
        competitionFormatColor: this.getCompetitionFormatColor(tournament),
        ratedLabel: this.getRatedLabel(tournament),
        ratedIcon: this.getRatedIcon(tournament),
        ratedColor: this.getRatedColor(tournament),
        registrationText: capacity ? null : this.$t('home.onsiteRegistration'),
        players: capacity?.label ?? '',
        progress: capacity?.progress ?? '0%',
        background: tournament.publicClubBackground || tournament.publicClubBackgroundUrl || heroImage,
        route: this.buildTournamentRoute(tournament),
      };
    },

    buildTournamentRoute(tournament) {
      if (!tournament?.id || !tournament?.publicClubSlug) return null;

      return {
        name: 'TournamentInfo',
        params: {
          clubSlug: tournament.publicClubSlug,
          id: tournament.slug || tournament.id,
        },
      };
    },

    normalizeHomepageClub(club) {
      return {
        name: club.name || this.$t('home.defaultClub'),
        city: [club.city, club.country].filter(Boolean).join(', '),
        tournaments: Number(club.tournamentsCount || 0),
        players: Number(club.playersCount || 0),
        logo: smallestImageUrl(club.images?.avatar || club.avatarUrl) || SERVICE_BRAND.logo,
        logoSrcset: imageSrcset(club.images?.avatar),
        background: club.images?.background || club.backgroundUrl || heroImage,
        route: this.buildClubRoute(club),
      };
    },

    buildClubRoute(club) {
      if (!club?.slug) return null;

      return {
        name: 'ClubHome',
        params: {
          clubSlug: club.slug,
        },
      };
    },

    normalizeHomepageResultItem(item) {
      const controlType = item.controlType || null;
      const shortDate = this.formatHomepageResultDate(item.tournamentDate) || item.date || '';

      return {
        ...item,
        avatar: item.avatarUrl || item.avatar || null,
        date: shortDate,
        control: item.control || CONTROL_LABELS[controlType] || '',
        delta: item.delta || '0',
        deltaValue: this.parseDeltaValue(item.deltaValue ?? item.delta),
        rating: item.rating ?? item.ratingFinal ?? null,
        route: this.buildPublicTournamentRoute(item),
        clubRoute: this.buildPublicClubRoute(item),
      };
    },

    parseDeltaValue(value) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      if (value == null) return 0;

      const parsed = Number(String(value).replace(',', '.').replace(/[^\d.+-]/g, ''));
      return Number.isFinite(parsed) ? parsed : 0;
    },

    getDeltaClass(item) {
      const value = Number(item?.deltaValue ?? 0);

      if (value > 0) return 'homepage-rating-delta--positive';
      if (value < 0) return 'homepage-rating-delta--negative';
      return 'homepage-rating-delta--neutral';
    },

    buildPublicTournamentRoute(item) {
      const tournamentId = item?.tournamentId || item?.id;
      const clubSlug = item?.clubSlug;
      if (!tournamentId || !clubSlug) return null;

      return {
        name: 'TournamentInfo',
        params: {
          clubSlug,
          id: item?.tournamentSlug || tournamentId,
        },
      };
    },

    buildPublicClubRoute(item) {
      if (!item?.clubSlug) return null;

      return {
        name: 'ClubHome',
        params: {
          clubSlug: item.clubSlug,
        },
      };
    },

    getTournamentDateBadge(dateString) {
      const tournamentDate = this.parseLocalDate(dateString);
      const today = this.parseLocalDate(this.getTodayDateString());

      if (!tournamentDate || !today) return this.$t('home.soon');

      const diffDays = Math.round((tournamentDate.getTime() - today.getTime()) / 86400000);
      if (diffDays <= 0) return this.$t('home.today');
      if (diffDays === 1) return this.$t('home.tomorrow');
      if (diffDays === 2) return this.$t('home.inTwoDays');

      return this.$t('home.inDays', { count: diffDays });
    },

    formatTournamentDate(dateString) {
      const formatted = formatDateUtil(dateString, 'day-month-long');
      return formatted === '—' ? '' : formatted;
    },

    formatTournamentTime(timeString) {
      if (!timeString || typeof timeString !== 'string') return '';

      const [hours, minutes] = timeString.split(':');
      if (hours == null || minutes == null) return '';

      return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
    },

    formatHomepageResultDate(dateString) {
      const formatted = formatDateUtil(dateString, 'day-month-short');
      return formatted === '—' ? '' : formatted;
    },

    getTournamentControlLabel(tournament) {
      const controlType = tournament.controlType ?? tournament.getControlType?.() ?? null;
      const control = CONTROL_LABELS[controlType] || this.$t('home.control');
      const settings = tournament.controlSettings
        || tournament.getSwissSettings?.()
        || tournament.getRoundRobinSettings?.()
        || tournament.getEliminationSettings?.()
        || {};
      const minutes = settings.timeMinutes;
      const increment = settings.increment;

      if (minutes == null) return control;

      return `${control} ${minutes}+${increment ?? 0}`;
    },

    getTournamentCapacity(tournament) {
      const limit = Number(tournament?.participantLimit);
      if (!Number.isFinite(limit) || limit <= 0) return null;

      const registered = Math.max(0, Number(tournament?.playersCount) || 0);
      const progress = Math.min(100, (registered / limit) * 100);

      return {
        label: `${registered}/${limit}`,
        progress: `${progress}%`,
      };
    },

    getCompetitionFormatLabel(tournament) {
      return tournament?.competitionFormat === CompetitionFormat.TEAM
        ? this.$t('home.competitionTeam')
        : this.$t('home.competitionIndividual');
    },

    getCompetitionFormatIcon(tournament) {
      return tournament?.competitionFormat === CompetitionFormat.TEAM ? 'bi bi-people-fill' : 'bi bi-person-fill';
    },

    getCompetitionFormatColor(tournament) {
      return tournament?.competitionFormat === CompetitionFormat.TEAM
        ? 'var(--tournament-format-team)'
        : 'var(--tournament-format-individual)';
    },

    getRatedLabel(tournament) {
      return tournament?.rated ? this.$t('home.rated') : this.$t('home.friendly');
    },

    getRatedIcon(tournament) {
      return tournament?.rated ? 'bi bi-patch-check' : 'bi bi-cup-hot';
    },

    getRatedColor(tournament) {
      return tournament?.rated ? 'var(--tournament-format-individual)' : 'var(--tournament-rating-friendly)';
    },

    getTodayDateString() {
      const date = new Date();
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    parseLocalDate(dateString) {
      if (!dateString || typeof dateString !== 'string') return null;
      const [year, month, day] = dateString.split('-').map(Number);

      if (!year || !month || !day) return null;

      return new Date(year, month - 1, day);
    },

    updateSearchQuery(query) {
      this.searchQuery = query;
      this.isSearchDropdownVisible = false;
    },

    async submitSearch(query = this.searchQuery) {
      const isCurrentSource = this.captureDataSource();
      const normalizedQuery = query.trim();

      this.submittedSearchQuery = normalizedQuery;
      if (!normalizedQuery) {
        this.submittedSearchResults = [];
        this.submittedSearchResultsCount = 0;
        this.isSearchDropdownVisible = false;
        return;
      }

      if (this.dataSource === 'mock') {
        const response = await MockService.search(normalizedQuery, null, { page: 1, pageSize: 6 });
        if (!isCurrentSource()) return;
        this.submittedSearchResults = response.data;
        this.submittedSearchResultsCount = response.total;
      } else {
        const response = await searchPublicItemsApi({
          query: normalizedQuery,
          language: this.$i18n.locale,
          page: 1,
          pageSize: 6,
          scope: 'future',
        });
        if (!isCurrentSource()) return;
        this.submittedSearchResults = response.data;
        this.submittedSearchResultsCount = response.total;
      }
      this.isSearchDropdownVisible = Boolean(normalizedQuery);
    },

    getEventBackground(event) {
      const image = event.background || heroImage;

      return `linear-gradient(180deg, rgba(0,0,0,.12), rgba(0,0,0,.92)), ${imageBackground(image, 360, 228)}`;
    },

    getResultControlIconComponent(result) {
      return this.getControlIconComponent(result?.controlType);
    },

    getControlIconComponent(controlType) {
      return RATING_CONTROL_ICON_MAP?.[RatingType.ELO_T]?.[controlType] || null;
    },

    getResultControlIconSize(result) {
      return RESULT_CONTROL_ICON_SIZES[result?.controlType] || '14px';
    },

    getEventControlIconSize(event) {
      return EVENT_CONTROL_ICON_SIZES[event?.controlType] || '12px';
    },

    getEventChessVariantWidth(event) {
      return event?.chessVariant === ChessVariant.CHESS960 ? null : '12px';
    },

    getSystemBadgeClass(system) {
      return TOURNAMENT_SYSTEM_BADGE_CLASS_MAP[system] || '';
    },

    getResultControlIconClass(result) {
      return result?.controlType ? `homepage-result-row__control-icon--${result.controlType}` : '';
    },

    getResultChessVariantLabel(result) {
      return this.getChessVariantLabel(result?.chessVariant);
    },

    getChessVariantLabel(chessVariant) {
      if (!chessVariant) return this.$t('home.chessVariantMissing');

      const key = `chessVariants.${chessVariant}`;
      const translated = this.$t(key);
      return translated === key ? chessVariant : translated;
    },


  },
};
